import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Step, Wizard } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { useSuggestSubtasks } from "~/repository";
import { BATCH_ADD_SUBTASKS_ERROR } from "~/store/action_types";
import { batchAddSubtasks } from "~/store/actions/actions.subtasks";
import { getConfigOption } from "~/store/selectors/selector.configOptions";
import { getProjectById, getProjectNameSelector } from "~/store/selectors/selector.projects";
import { getTaskListForTaskId } from "~/store/selectors/selector.taskLists";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { stripHtml } from "~/utils/utils";
import { AddSuggestionsStep } from "./AddSuggestionsStep";
import { prepareSubtaskPayload } from "./suggestionUtils";
import { SuggestSubtasksStep } from "./SuggestSubtasksStep";

export const SuggestSubtasksWizard = ({ open, onCancel, projectId, taskId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const task = useSelector((state) => getTaskById(state, taskId));
  const tasklist = useSelector((state) => getTaskListForTaskId(state, projectId, taskId));
  const projectName = useSelector((state) => getProjectNameSelector(state, projectId));
  const project = useSelector((state) => getProjectById(state, projectId));
  const showTaskNumber = useSelector((state) => getConfigOption(state, `show_task_id`));

  const [suggestions, setSuggestions] = useState([]);
  const [promptId, setPromptId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate, isLoading, isError } = useSuggestSubtasks(projectId, taskId);

  const handleSuggestSubtasks = useCallback(
    (data) => {
      mutate(data, {
        onSuccess: ({ prompt_id, suggested_subtasks }) => {
          setSuggestions(suggested_subtasks);
          setPromptId(prompt_id);
        },
        onError: (err) => {
          if (err?.status_code === 429) {
            return showErrorToast(t("Too many requests."));
          }
          return showErrorToast(t("Unable to suggest subtasks."));
        },
      });
    },
    [mutate, t]
  );

  const handleSubmit = useCallback(
    (subtasks) => {
      setIsSubmitting(true);
      const preparedSubtasks = prepareSubtaskPayload(subtasks);
      dispatch(batchAddSubtasks(projectId, taskId, preparedSubtasks)).then((response) => {
        setIsSubmitting(false);
        if (response.type === BATCH_ADD_SUBTASKS_ERROR) {
          showErrorToast(t("Unable to add tasks."));
        } else {
          if (onCancel) onCancel();
        }
      });
    },
    [dispatch, onCancel, projectId, t, taskId]
  );

  const taskName = showTaskNumber ? `#${task?.get("task_number")}: ${task?.get("name")}` : task?.get("name");
  const cleanedDescription = task?.get("body_formatted") ? stripHtml(task?.get("body_formatted")) : "";

  return (
    <Wizard
      open={open}
      onClose={onCancel}
      dialogClassName="tw-max-w-[680px] tw-w-full"
    >
      <Step
        subtitle={t("Suggest Subtasks")}
        shouldRenderNextButton={false}
      >
        <SuggestSubtasksStep
          projectId={projectId}
          taskId={taskId}
          description={cleanedDescription}
          name={taskName}
          taskListName={tasklist?.get("name")}
          projectName={projectName}
          projectDescription={project?.get("body_formatted")}
          onNextButtonClick={handleSuggestSubtasks}
        />
      </Step>
      <Step
        subtitle={t("Subtask Suggestions")}
        isLast
        shouldRenderNextButton={false}
      >
        <AddSuggestionsStep
          projectId={projectId}
          loading={isLoading}
          error={isError}
          suggestions={suggestions}
          promptId={promptId}
          submitting={isSubmitting}
          onSubmit={handleSubmit}
          infoBoxMessage={t(
            "ActiveCollab recommends subtasks for your task based on the task description and project details. Like the suggestions? Give them a thumbs up."
          )}
          buttonText={t("Add Subtask")}
          selectionMode="daily"
        />
      </Step>
    </Wizard>
  );
};

SuggestSubtasksWizard.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  description: PropTypes.string,
};
